/**
 * Creates binding utility functions with props taking precedence over context.
 * Validates sources only once during initialization for better performance.
 *
 * @param {object} props - The props object containing optional binding functions
 * @param {object} context - The context object containing optional binding functions
 *
 * @returns {object} An object with getBinding, getBindings, and getBindingsList functions
 */
export const createBindingUtils = (props = {}, context = {}) => {
  const getBinding = props.getBinding || context.getBinding
  const getBindings = props.getBindings || context.getBindings
  const getBindingsList = props.getBindingsList || context.getBindingsList

  return {
    /**
     * Gets a single binding
     * @param {string} id - The binding ID
     * @returns {any} The bound value or undefined
     */
    getBinding,

    /**
     * Gets multiple bindings
     * @param {string[]} ids - Array of binding IDs
     * @returns {any[]} Array of bound values
     */
    getBindings,

    /**
     * Gets the list of available bindings
     * @returns {string[]} Array of binding IDs
     */
    getBindingsList,
  }
}

export const resolveBindingValue = (itemId, name, context, props = {}) => {
  if (!itemId) return props[name]
  const key = getItemsBindName(itemId, name)
  if (context[key] !== undefined) return context[key]
  if (props[key] !== undefined) return props[key]

  if (itemId.includes(' -- ')) {
    return resolveBindingValue(
      itemId.split(' -- ').slice(0, -1).join(' -- '),
      name,
      context,
      props
    )
  }
  if (itemId.includes(' - ')) {
    return resolveBindingValue(itemId.split(' - ')[0], name, context, props)
  }
  return props[name] || context[name]
}

export const getItemsBindName = (itemId, name) => `${name}_${itemId}`

export const resolveBindingValues = (
  itemId,
  names = [],
  context = {},
  props = {}
) =>
  Object.fromEntries(
    names.map(name => [
      [name],
      resolveBindingValue(itemId, name, context, props),
    ])
  )

export const getBindingsFunc = self => {
  const { pushId } = self.props
  return resolveBindingValues(
    pushId,
    ['getBinding', 'getBindings', 'getBindingsList'],
    self.context,
    self.props
  )
}

export const getBindingsFromProps = (props = {}) => {
  const { getBinding, getBindings, getBindingsList } = props
  return { getBinding, getBindings, getBindingsList }
}
